import React from 'react';
import NewBorrowerFormFieldset from '../NewBorrowerFormFieldset';
import { Box, DialogContentText, Grid, Typography } from '@mui/material';
import SolarProposalFileDropzone from '../SolarProposalFileDropzone';
import { InitialValues } from '../schema';
import { Control, UseFormSetError, useWatch } from 'react-hook-form';
import { CustomTooltip } from '../../../../CustomTooltip';
import { InfoIcon } from '../../../../loan_application/icons/InfoIcon';
import { DocumentUploadFieldset } from './DocumentUploadFieldset';
import InputField from '../../../../hook_form_fields/InputField';
import { RequiredDocument } from '../../../../../../../../types/api';
import { Tracker } from '../../../../../utils/tracker';
import RequiredDocumentAPI from '../../../../../services/apis/RequiredDocumentAPI';
import { Asset } from '../../../../../modules/loan/asset';
import { Installer } from '../../../../../modules/installer';
const { BorrowerForm } = Installer.UI;

const tracker = Tracker.getInstance();

const triggerAnalysis = async (document: RequiredDocument, name: any) => {
  if (document && document.files?.length > 0) {
    const payload = {
      documentId: document.files[0].id,
      documentType: name,
    };
    tracker.track(Tracker.EVENTS.ORACLE_DOC_ANALYSIS_REQUEST, payload);
    const result = await RequiredDocumentAPI.analyzeDocument(
      document.files[0].id,
      true // isDraft
    ).catch((error) => {
      tracker.track(Tracker.EVENTS.ORACLE_DOC_ANALYSIS_REQUEST_FAILED, {
        ...payload,
        error: error.message,
      });
      return Promise.reject(error);
    });

    const validations = Array.isArray(result?.result?.output?.validations)
      ? result.result.output.validations
      : [];
    const validation = validations.filter(
      (v: any) => v.id == 'is_document_recognized'
    )[0];

    tracker.track(Tracker.EVENTS.ORACLE_DOC_ANALYSIS_REQUEST_SUCCESS, payload);

    if (validation?.result === true)
      tracker.track(
        Tracker.EVENTS.ORACLE_DOC_ANALYSIS_VALIDATION_DOC_RECOGNIZED_TRUE,
        { ...payload, message: validation.message }
      );
    else if (validation?.result === false)
      tracker.track(
        Tracker.EVENTS.ORACLE_DOC_ANALYSIS_VALIDATION_DOC_RECOGNIZED_FALSE,
        { ...payload, message: validation.message }
      );
    else
      tracker.track(
        Tracker.EVENTS.ORACLE_DOC_ANALYSIS_REQUEST_INVALID_RESPONSE_FORMAT,
        { ...payload, message: JSON.stringify(result) }
      );
    return result;
  }
  return undefined;
};

type DocumentsUploadSectionProps = {
  control: Control<InitialValues>;
  setError: UseFormSetError<InitialValues>;
  onFileChange?: any;
  onFileRemove?: any;
};

export const DocumentsUploadSection = ({
  control,
  setError,
  onFileChange,
  onFileRemove,
}: DocumentsUploadSectionProps) => {
  const projectAssets = useWatch({ control, name: 'projectAssets' });
  const selectedAssetTypes = Asset.getSelectedTypes(projectAssets);
  // Flex installer who picked only non-solar improvements: utility bills
  // aren't needed (no Solar system to size against historical usage).
  const showUtilityBills =
    BorrowerForm.isUtilityBillsSectionVisible(selectedAssetTypes);
  // Label + tooltip mirror the doc type the upload will be saved as.
  const proposalDocLabel =
    BorrowerForm.proposalDocumentType(selectedAssetTypes);
  const hasSolarAsset = BorrowerForm.hasSolarAsset(selectedAssetTypes);

  const proposalTooltip = !hasSolarAsset
    ? 'The proposal should include the project scope, the total cost, and any benefits the borrower will receive from the project.'
    : 'The proposal should include the system size, system cost, year one production estimate, and all benefits the borrower will receive from the system, such as utility savings, ITC, and depreciation.';

  const utilityBillAdditionalInfo =
    'Please upload complete utility bill(s) that show 12 months of usage for the property where the system will be installed. Up to 12 documents may be uploaded if needed.';

  return (
    <NewBorrowerFormFieldset title={'Documents Upload'}>
      <Grid container maxWidth="md" spacing={2}>
        <Grid item xs={12} sm={12}>
          <SolarProposalFileDropzone
            control={control}
            setError={setError}
            name="loan_proposal"
            required
            label={proposalDocLabel}
            tooltip={proposalTooltip}
            onFileChange={onFileChange}
            onUploadResponse={triggerAnalysis}
          />
        </Grid>
        {showUtilityBills && (
          <Grid item xs={12}>
            <DocumentUploadFieldset
              control={control}
              label="Utility Bills"
              required
              uploadMoreText="+ Add Additional Utility Bill (Optional)"
              additionalInfo={utilityBillAdditionalInfo}
              max={12}
              name={'utility_bills'}
              onFileChange={onFileChange}
              onFileRemove={onFileRemove}
              onUploadResponse={triggerAnalysis}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <DocumentUploadFieldset
            control={control}
            label={
              <Box>
                <Box display="flex">
                  <Typography variant="body1">
                    Supporting documents for state or local incentives
                    (Optional)
                  </Typography>
                  <CustomTooltip
                    title={
                      'Examples of such incentives include among others, grants, REC programs, utility incentives, and state or local tax credits.'
                    }
                  >
                    <InfoIcon
                      sx={{
                        fontSize: '.75rem',
                        marginTop: '.3125rem',
                        marginLeft: '.25rem',
                        verticalAlign: 'center',
                      }}
                    />
                  </CustomTooltip>
                </Box>
                <DialogContentText>
                  If the project is eligible for state or local incentives,
                  please upload supporting documents for the applicability of
                  such incentives. While not required to initiate an
                  application, such documentation may be required to complete
                  underwriting if the incentive is significant. Providing this
                  documentation now will shorten the underwriting process.
                </DialogContentText>
              </Box>
            }
            uploadMoreText="+ Add Additional Supporting Document"
            max={5}
            name={'incentive_documents'}
            onFileChange={onFileChange}
            onFileRemove={onFileRemove}
            onUploadResponse={triggerAnalysis}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <InputField
            control={control}
            name="application.deal_description_and_context"
            multiline
            minRows={2}
            label={
              <Box>
                <Box display="flex">
                  <Typography>
                    Deal Description and Context (Optional)
                  </Typography>
                </Box>
                <DialogContentText>
                  Please provide any additional information that will help the
                  underwriting team understand the proposed project. Examples of
                  helpful information include who will ultimately benefit from
                  the project (the host, one or multiple offtakers, etc.),
                  whether multiple or unique properties are involved, any
                  complicating factors you are aware of in the borrower&apos;s
                  ownership structure or business model, any special state or
                  local benefits that may be available, a status update if the
                  project has already begun construction, and any other
                  additional context you wish to provide that may streamline
                  underwriting.
                </DialogContentText>
              </Box>
            }
            maxLength={5000}
            counter
          />
        </Grid>
      </Grid>
    </NewBorrowerFormFieldset>
  );
};
